import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { ChevronLeft, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Constants for carousel and impression tracking
const ADS_PER_PAGE = 4;
const TOTAL_ADS = 16;
const AUTO_ROTATE_INTERVAL = 5000; // 5 seconds - FIXED rotation interval
const MIN_IMPRESSION_DWELL = 1500; // 1.5 seconds for valid impression

// Tablet dimensions (standard Android tablet 16:10 aspect ratio)
const TABLET_WIDTH = 1280;
const TABLET_HEIGHT = 800;

// Booma Logos
const BOOMA_LOGO_INVERTED = "https://customer-assets.emergentagent.com/job_510d4ad4-6c30-4bd6-a7e0-e51838ecf5ce/artifacts/vmrblhbh_booma_logo-3-invert.jpg";

// Demo Business Ad Images (fallback when API fails)
const DEMO_AD_IMAGES = {
  "Sydney Coffee Co": "https://static.prod-images.emergentagent.com/jobs/35b12a18-38ba-4e6e-b1b6-90345e4b13b1/images/6f3bc4553eac19a3c09c1e6762cfca43ca7c74aaaf54ecd788be4861e2eb12a7.png",
  "Melbourne Pizza House": "https://static.prod-images.emergentagent.com/jobs/35b12a18-38ba-4e6e-b1b6-90345e4b13b1/images/ae707c420b32a559dfc5ae01a13136a3338cceea9a95f493082d868924c2ac5b.png",
  "Brisbane Fitness Club": "https://static.prod-images.emergentagent.com/jobs/35b12a18-38ba-4e6e-b1b6-90345e4b13b1/images/5f9f44a6f2ec0ace3123ea6d5134f0188f519c1769ef1fd83f97683d3131f82a.png",
  "Adelaide Wine Bar": "https://static.prod-images.emergentagent.com/jobs/35b12a18-38ba-4e6e-b1b6-90345e4b13b1/images/2ed5b7398b94ed234e8d4a3ae48232083bce20fc4552b91051e7a03d12156b96.png",
  "St Kilda Beach Bar": "https://static.prod-images.emergentagent.com/jobs/35b12a18-38ba-4e6e-b1b6-90345e4b13b1/images/f4e9827988e3118bcd7f1c916edb259b412beb6b733a8ee72d363bb92f1d27d7.png",
  "South Yarra Salon": "https://static.prod-images.emergentagent.com/jobs/35b12a18-38ba-4e6e-b1b6-90345e4b13b1/images/a14856198038e2e661cb440c2feed51d6d199956dc89b75cec0ca7f208f23e41.png",
  "Fitzroy Street Tattoo": "https://static.prod-images.emergentagent.com/jobs/35b12a18-38ba-4e6e-b1b6-90345e4b13b1/images/7ac3a32d2cce588e67f92e4f2e82ad3bbfd43b1c6e8a542b1d93f67ee12c2db1.png",
  "Perth Auto Detailing": "https://static.prod-images.emergentagent.com/jobs/35b12a18-38ba-4e6e-b1b6-90345e4b13b1/images/6f3bc4553eac19a3c09c1e6762cfca43ca7c74aaaf54ecd788be4861e2eb12a7.png",
  // Melbourne businesses - placeholders with branded colors
  "Richmond Café": "https://placehold.co/600x300/D97706/FFFFFF?text=Richmond+Cafe",
  "Prahran Market Deli": "https://placehold.co/600x300/059669/FFFFFF?text=Prahran+Deli",
  "Chapel Street Fashion": "https://placehold.co/600x300/7C3AED/FFFFFF?text=Chapel+St+Fashion",
  "Toorak Road Spa": "https://placehold.co/600x300/EC4899/FFFFFF?text=Toorak+Spa",
  "Albert Park Running": "https://placehold.co/600x300/0EA5E9/FFFFFF?text=Albert+Park+Running",
  "Port Melbourne Brewery": "https://placehold.co/600x300/B45309/FFFFFF?text=Port+Melbourne+Brewery",
  "Southbank Steakhouse": "https://placehold.co/600x300/DC2626/FFFFFF?text=Southbank+Steakhouse",
  "Docklands Gym": "https://placehold.co/600x300/2563EB/FFFFFF?text=Docklands+Gym",
  "Carlton Italian": "https://placehold.co/600x300/16A34A/FFFFFF?text=Carlton+Italian",
  "Fitzroy Art Gallery": "https://placehold.co/600x300/8B5CF6/FFFFFF?text=Fitzroy+Gallery"
};

// Dark map style for Google Maps
const darkMapStyles = [
  { elementType: "geometry", stylers: [{ color: "#212121" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#212121" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#757575" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#181818" }] },
  { featureType: "road", elementType: "geometry.fill", stylers: [{ color: "#2c2c2c" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#8a8a8a" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#373737" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#3c3c3c" }] },
  { featureType: "road.highway.controlled_access", elementType: "geometry", stylers: [{ color: "#4e4e4e" }] },
  { featureType: "transit", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#000000" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#3d3d3d" }] }
];

// Clean map options - no Google branding or controls
const cleanMapOptions = {
  styles: darkMapStyles,
  disableDefaultUI: true,
  zoomControl: false,
  mapTypeControl: false,
  scaleControl: false,
  streetViewControl: false,
  rotateControl: false,
  fullscreenControl: false,
  clickableIcons: false,
};

// Simulated trip data - Melbourne route passes through multiple advertiser vicinities
const DEMO_TRIPS = [
  {
    id: "trip-melbourne-geo-001",
    pickup: { lat: -37.8136, lng: 144.9631, name: "Melbourne CBD" },
    destination: { lat: -37.8676, lng: 144.9741, name: "St Kilda Beach" },
    passenger: "Alex M.",
    eta: "15 mins",
    distance: "7.2 km",
    waypoints: [
      { lat: -37.8136, lng: 144.9631 },
      { lat: -37.8250, lng: 144.9750 },
      { lat: -37.8387, lng: 144.9930 },
      { lat: -37.8500, lng: 144.9850 },
      { lat: -37.8600, lng: 144.9800 },
      { lat: -37.8676, lng: 144.9741 },
    ]
  },
  {
    id: "trip-sydney-001",
    pickup: { lat: -33.8688, lng: 151.2093, name: "Sydney CBD" },
    destination: { lat: -33.8915, lng: 151.2767, name: "Bondi Beach" },
    passenger: "Sarah M.",
    eta: "12 mins",
    distance: "4.2 km",
    waypoints: [
      { lat: -33.8688, lng: 151.2093 },
      { lat: -33.8750, lng: 151.2300 },
      { lat: -33.8850, lng: 151.2550 },
      { lat: -33.8915, lng: 151.2767 }
    ]
  },
  {
    id: "trip-brisbane-001",
    pickup: { lat: -27.4705, lng: 153.0260, name: "Brisbane CBD" },
    destination: { lat: -27.4780, lng: 153.0280, name: "South Bank" },
    passenger: "Emma L.",
    eta: "5 mins",
    distance: "1.8 km",
    waypoints: [
      { lat: -27.4705, lng: 153.0260 },
      { lat: -27.4750, lng: 153.0270 },
      { lat: -27.4780, lng: 153.0280 }
    ]
  }
];

// Ad Tile Component with modern styling and click tracking
const AdTile = ({ ad, position, boomaLogo, onAdClick, tripId, onSaveDeal, onDealModalOpen, onDealModalClose }) => {
  const [showDealModal, setShowDealModal] = useState(false);
  const [savingDeal, setSavingDeal] = useState(false);
  const [dealSaved, setDealSaved] = useState(false);

  const handleClick = async () => {
    if (ad) {
      // If this ad has a deal available, show the deal modal
      if (ad.deal_available) {
        setShowDealModal(true);
        onDealModalOpen?.();
        return;
      }

      // Otherwise record a regular click
      try {
        await axios.post(`${API}/ad/event`, {
          event_type: "CLICK",
          tracking_token: ad.tracking_token,
          trip_id: tripId,
          session_id: `session-${tripId}`,
          placement: "driver_panel",
          passenger_id: "demo-passenger-123"
        });
        console.log(`Click recorded for ${ad.business_name}`);
      } catch (error) {
        console.error("Failed to record click:", error);
      }
      if (onAdClick) {
        onAdClick(ad.business_name);
      }
    }
  };

  const handleCloseDealModal = () => {
    setShowDealModal(false);
    onDealModalClose?.();
  };

  const handleSaveDeal = async () => {
    if (!ad?.tracking_token) {
      console.error("No tracking token available");
      return;
    }

    setSavingDeal(true);
    try {
      const response = await axios.post(`${API}/ad/event`, {
        event_type: "SAVE_TO_CART",
        tracking_token: ad.tracking_token,
        trip_id: tripId,
        session_id: `session-${tripId}`,
        placement: "driver_panel",
        passenger_id: "demo-passenger-123"
      });

      if (response.data.processed) {
        setDealSaved(true);
        handleCloseDealModal();
        if (onSaveDeal) {
          onSaveDeal({
            businessName: ad.business_name,
            dealId: response.data.deal_id,
            expiresAt: response.data.deal_expires_at
          });
        }
      }
    } catch (error) {
      console.error("Failed to save deal:", error);
    } finally {
      setSavingDeal(false);
    }
  };

  const getImageUrl = () => {
    if (ad?.creative?.file_url) {
      return ad.creative.file_url;
    }
    if (ad?.business_name && DEMO_AD_IMAGES[ad.business_name]) {
      return DEMO_AD_IMAGES[ad.business_name];
    }
    return boomaLogo;
  };

  const getTierBadgeColor = () => {
    switch (ad?.tier?.toUpperCase()) {
      case 'GOLD': return 'bg-yellow-500 text-black';
      case 'SILVER': return 'bg-gray-400 text-black';
      case 'BRONZE': return 'bg-amber-700 text-white';
      default: return 'bg-gray-600 text-white';
    }
  };

  return (
    <>
      <div 
        className="relative overflow-hidden rounded-2xl bg-gray-900 shadow-2xl cursor-pointer transition-transform hover:scale-[1.02] active:scale-[0.98]"
        style={{ height: '100%' }}
        data-testid={`tile-${position}`}
        onClick={handleClick}
      >
        <img 
          src={getImageUrl()} 
          alt={ad?.business_name || "Booma"}
          className="w-full h-full object-cover rounded-2xl"
          onError={(e) => { e.target.src = boomaLogo; }}
        />
        
        {/* Tier Badge */}
        {ad?.tier && (
          <div className={`absolute top-3 left-3 px-3 py-1 rounded text-xs font-bold ${getTierBadgeColor()}`}>
            {ad.tier.toUpperCase()}
          </div>
        )}
        
        {/* Deal Badge */}
        {ad?.deal_available && !dealSaved && (
          <div className="absolute top-3 right-3 bg-amber-500 text-black px-3 py-1.5 rounded-full text-sm font-bold flex items-center gap-1 animate-pulse">
            <span>🎁</span> DEAL
          </div>
        )}
        
        {/* Saved Badge */}
        {dealSaved && (
          <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1.5 rounded-full text-sm font-bold flex items-center gap-1">
            ✓ SAVED
          </div>
        )}

        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-4 rounded-b-2xl">
          <p className="text-white font-bold text-lg truncate">{ad?.business_name || "Booma"}</p>
          {ad?.offer_text && (
            <p className="text-amber-400 text-sm font-semibold truncate">{ad.offer_text}</p>
          )}
          {!ad?.offer_text && ad?.cta_text && (
            <p className="text-gray-300 text-sm truncate">{ad.cta_text}</p>
          )}
          {ad?.distance_meters && (
            <p className="text-green-400 text-sm mt-1">{Math.round(ad.distance_meters)}m away</p>
          )}
        </div>
      </div>

      {/* Deal Modal */}
      {showDealModal && (
        <div 
          className="fixed inset-0 bg-black/80 z-[9999] flex items-center justify-center p-4"
          onClick={handleCloseDealModal}
        >
          <div 
            className="bg-gray-900 rounded-2xl max-w-md w-full overflow-hidden shadow-2xl border border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Deal Image */}
            <div className="h-48 relative">
              <img 
                src={getImageUrl()} 
                alt={ad?.business_name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent" />
              <button 
                onClick={handleCloseDealModal}
                className="absolute top-4 right-4 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center text-white text-xl hover:bg-black/70 transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Deal Content */}
            <div className="p-6">
              <h3 className="text-2xl font-bold text-white mb-2">{ad?.business_name}</h3>
              
              {ad?.deal_description && (
                <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg p-4 mb-4">
                  <p className="text-amber-400 font-semibold text-xl">{ad.deal_description}</p>
                </div>
              )}

              {ad?.distance_meters && (
                <p className="text-gray-400 text-base mb-4">
                  📍 {Math.round(ad.distance_meters)}m away
                </p>
              )}

              <div className="bg-gray-800 rounded-lg p-4 mb-4">
                <p className="text-gray-400 text-sm mb-1">How it works:</p>
                <p className="text-gray-300 text-base">
                  Save this deal to your Booma app. Show the code at {ad?.business_name} to redeem.
                </p>
                <p className="text-amber-400 text-sm mt-2">⏱️ Expires 24 hours after saving</p>
              </div>

              <button
                onClick={handleSaveDeal}
                disabled={savingDeal}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-4 px-6 rounded-xl text-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingDeal ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    Saving...
                  </>
                ) : (
                  <>
                    <span>💾</span>
                    Save to My Deals
                  </>
                )}
              </button>

              <p className="text-gray-500 text-sm text-center mt-3">
                Deal will appear in your Booma passenger app
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Page Indicator Component
const PageIndicator = ({ currentPage, totalPages }) => (
  <div className="flex items-center justify-center gap-2 py-3">
    {Array.from({ length: totalPages }, (_, i) => (
      <div 
        key={i}
        className={`h-3 rounded-full transition-all duration-300 ${
          i === currentPage 
            ? 'bg-white w-8' 
            : 'bg-gray-600 w-3 hover:bg-gray-500'
        }`}
      />
    ))}
  </div>
);

export default function DrivePanel() {
  // Display state - what's currently shown (stable for 5 seconds)
  const [displayAds, setDisplayAds] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  
  // Buffer state - incoming ads from API (applied on rotation)
  const bufferedAdsRef = useRef([]);
  
  const [currentTrip, setCurrentTrip] = useState(DEMO_TRIPS[0]);
  const [vehiclePosition, setVehiclePosition] = useState(null);
  const [time, setTime] = useState(new Date());
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false); // Pause for deal modal
  const [clickFeedback, setClickFeedback] = useState(null);
  const [dealFeedback, setDealFeedback] = useState(null);
  const [adLog, setAdLog] = useState([]);
  const [spotifyTrack] = useState({
    title: "Daylight",
    artist: "David Kushner",
    progress: 45
  });
  
  // Refs for animation and tracking
  const animationRef = useRef(null);
  const progressRef = useRef(0);
  const lastFetchPosition = useRef(null);
  const mapRef = useRef(null);
  const autoRotateRef = useRef(null);
  const pageStartTimeRef = useRef(null);
  const recordedImpressionsRef = useRef(new Set()); // Track ad_ids with valid impressions this trip

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  // Get current page ads from DISPLAY state (not buffer)
  const currentPageAds = displayAds.slice(currentPage * ADS_PER_PAGE, (currentPage + 1) * ADS_PER_PAGE);
  const totalPages = Math.max(Math.ceil(displayAds.length / ADS_PER_PAGE), 1);

  // Record impression for an ad (only if not already recorded this trip and dwell time met)
  const recordImpression = useCallback(async (ad, dwellTime) => {
    if (!ad?.tracking_token || !ad?.ad_id) return;
    
    const adKey = ad.ad_id || ad.tracking_token;
    
    // Check if already recorded this trip
    if (recordedImpressionsRef.current.has(adKey)) {
      console.log(`Impression already recorded for ${ad.business_name} this trip`);
      return;
    }

    // Validate minimum dwell time
    if (dwellTime < MIN_IMPRESSION_DWELL) {
      console.log(`Dwell time ${dwellTime}ms < ${MIN_IMPRESSION_DWELL}ms for ${ad.business_name}, not recording`);
      return;
    }

    try {
      await axios.post(`${API}/ad/event`, {
        event_type: "EXPOSURE",
        tracking_token: ad.tracking_token,
        trip_id: currentTrip.id,
        session_id: `session-${currentTrip.id}`,
        placement: "driver_panel",
        passenger_id: "demo-passenger-123",
        metadata: {
          dwell_time_ms: dwellTime,
          page_position: currentPage + 1,
          advertiser_tier: ad.tier
        }
      });
      
      // Mark as recorded
      recordedImpressionsRef.current.add(adKey);
      
      console.log(`✓ Impression recorded: ${ad.business_name} (${ad.tier}) - ${dwellTime}ms dwell`);
    } catch (error) {
      console.error("Failed to record impression:", error);
    }
  }, [currentTrip.id, currentPage]);

  // Record impressions for current page ads when leaving page
  const recordPageImpressions = useCallback(() => {
    if (!pageStartTimeRef.current) return;
    
    const dwellTime = Date.now() - pageStartTimeRef.current;
    
    // Record impression for each ad on the current page
    currentPageAds.forEach(ad => {
      recordImpression(ad, dwellTime);
    });
  }, [currentPageAds, recordImpression]);

  // Navigate to next/previous page - ALSO applies buffered ads
  const goToPage = useCallback((newPage, applyBuffer = true) => {
    // Record impressions for current page before leaving
    recordPageImpressions();
    
    // Apply buffered ads if available (this is when display updates)
    if (applyBuffer && bufferedAdsRef.current.length > 0) {
      setDisplayAds(bufferedAdsRef.current);
      setAdLog(prev => [...prev.slice(-4), {
        time: new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        message: `Ads refreshed: ${bufferedAdsRef.current.slice(0, 3).map(a => a.business_name).join(', ')}...`
      }]);
    }
    
    // Calculate new page with wrapping
    const ads = applyBuffer && bufferedAdsRef.current.length > 0 ? bufferedAdsRef.current : displayAds;
    const pages = Math.max(Math.ceil(ads.length / ADS_PER_PAGE), 1);
    const targetPage = ((newPage % pages) + pages) % pages;
    
    setCurrentPage(targetPage);
    pageStartTimeRef.current = Date.now();
  }, [displayAds, recordPageImpressions]);

  const goToNextPage = useCallback(() => {
    goToPage(currentPage + 1);
  }, [currentPage, goToPage]);

  const goToPrevPage = useCallback(() => {
    goToPage(currentPage - 1);
  }, [currentPage, goToPage]);

  // Auto-rotate carousel - FIXED 5 second interval
  useEffect(() => {
    if (isPlaying && !isPaused && displayAds.length > ADS_PER_PAGE) {
      // Clear any existing interval
      if (autoRotateRef.current) {
        clearInterval(autoRotateRef.current);
      }
      
      // Set up new interval - exactly 5 seconds
      // Use a direct state update to avoid dependency on currentPage
      autoRotateRef.current = setInterval(() => {
        setCurrentPage(prevPage => {
          // Record impressions for current page before leaving
          if (pageStartTimeRef.current) {
            const dwellTime = Date.now() - pageStartTimeRef.current;
            // We'll record impressions in the next render cycle
            console.log(`Page ${prevPage + 1} displayed for ${dwellTime}ms`);
          }
          
          // Calculate next page with wrapping
          const totalPagesCalc = Math.max(Math.ceil(displayAds.length / ADS_PER_PAGE), 1);
          const nextPage = (prevPage + 1) % totalPagesCalc;
          
          // Reset page start time
          pageStartTimeRef.current = Date.now();
          
          return nextPage;
        });
      }, AUTO_ROTATE_INTERVAL);
      
      console.log("Auto-rotate started: every 5 seconds");
    } else {
      // Clear interval when not playing or paused
      if (autoRotateRef.current) {
        clearInterval(autoRotateRef.current);
        autoRotateRef.current = null;
        console.log("Auto-rotate stopped");
      }
    }
    
    return () => {
      if (autoRotateRef.current) {
        clearInterval(autoRotateRef.current);
        autoRotateRef.current = null;
      }
    };
  }, [isPlaying, isPaused, displayAds.length]);

  // Fetch inventory - stores in BUFFER, not display
  const fetchInventory = useCallback(async (lat, lng) => {
    try {
      const response = await axios.post(`${API}/ad/request`, {
        trip_id: currentTrip.id,
        session_id: `session-${currentTrip.id}-${Date.now()}`,
        placement: "driver_panel",
        current_lat: lat,
        current_lng: lng,
        destination_lat: currentTrip.destination.lat,
        destination_lng: currentTrip.destination.lng,
        passenger_id: "demo-passenger-123",
        driver_id: "demo-driver",
        vehicle_id: "demo-vehicle",
        panel_id: "demo-panel",
        number_of_ads: TOTAL_ADS
      });
      
      if (response.data.ads && response.data.ads.length > 0) {
        // Apply tier weighting and store in BUFFER (not display)
        const weightedAds = applyTierWeighting(response.data.ads);
        bufferedAdsRef.current = weightedAds;
        
        // If this is the first fetch (no display ads yet), apply immediately
        if (displayAds.length === 0) {
          setDisplayAds(weightedAds);
          setAdLog([{
            time: new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
            message: `Initial load: ${weightedAds.slice(0, 3).map(a => a.business_name).join(', ')}...`
          }]);
        }
      }
      lastFetchPosition.current = { lat, lng };
    } catch (error) {
      console.error("Failed to fetch inventory:", error);
      // Fallback to legacy endpoint
      try {
        const fallbackResponse = await axios.post(`${API}/ad-serving/inventory/panel`, {
          trip_id: currentTrip.id,
          passenger_id: "demo-passenger",
          driver_id: "demo-driver",
          vehicle_id: "demo-vehicle",
          panel_id: "demo-panel",
          current_lat: lat,
          current_lng: lng,
          pickup_lat: currentTrip.pickup.lat,
          pickup_lng: currentTrip.pickup.lng,
          destination_lat: currentTrip.destination.lat,
          destination_lng: currentTrip.destination.lng
        });
        if (fallbackResponse.data.tiles) {
          const legacyAds = fallbackResponse.data.tiles.map(t => t.ad).filter(Boolean);
          bufferedAdsRef.current = legacyAds;
          if (displayAds.length === 0) {
            setDisplayAds(legacyAds);
          }
        }
      } catch (fallbackError) {
        console.error("Fallback also failed:", fallbackError);
      }
    }
  }, [currentTrip, displayAds.length]);

  // Tier weighting algorithm - ensures Bronze still gets exposure
  const applyTierWeighting = (ads) => {
    const gold = ads.filter(a => a.tier?.toUpperCase() === 'GOLD');
    const silver = ads.filter(a => a.tier?.toUpperCase() === 'SILVER');
    const bronze = ads.filter(a => a.tier?.toUpperCase() === 'BRONZE');
    const other = ads.filter(a => !['GOLD', 'SILVER', 'BRONZE'].includes(a.tier?.toUpperCase()));

    const shuffleArray = (arr) => {
      const shuffled = [...arr];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    };

    const result = [];
    const allShuffled = [
      ...shuffleArray(gold),
      ...shuffleArray(silver),
      ...shuffleArray(bronze),
      ...shuffleArray(other)
    ];

    // Weighted selection: Gold 45%, Silver 35%, Bronze 20%
    const weightedSelect = () => {
      const rand = Math.random();
      if (rand < 0.45 && gold.length > 0) {
        return shuffleArray(gold)[0];
      } else if (rand < 0.80 && silver.length > 0) {
        return shuffleArray(silver)[0];
      } else if (bronze.length > 0) {
        return shuffleArray(bronze)[0];
      }
      return null;
    };

    const usedIds = new Set();
    for (let i = 0; i < ADS_PER_PAGE; i++) {
      let selected = weightedSelect();
      if (!selected || usedIds.has(selected.ad_id)) {
        selected = allShuffled.find(a => !usedIds.has(a.ad_id));
      }
      if (selected) {
        result.push(selected);
        usedIds.add(selected.ad_id);
      }
    }

    allShuffled.forEach(ad => {
      if (!usedIds.has(ad.ad_id) && result.length < TOTAL_ADS) {
        result.push(ad);
        usedIds.add(ad.ad_id);
      }
    });

    return result;
  };

  // Update clock
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Initialize trip
  useEffect(() => {
    if (currentTrip.waypoints && currentTrip.waypoints.length > 0) {
      setVehiclePosition({ lat: currentTrip.waypoints[0].lat, lng: currentTrip.waypoints[0].lng });
    } else {
      setVehiclePosition({ lat: currentTrip.pickup.lat, lng: currentTrip.pickup.lng });
    }
    progressRef.current = 0;
    setCurrentPage(0);
    pageStartTimeRef.current = Date.now();
    recordedImpressionsRef.current = new Set();
    bufferedAdsRef.current = [];
    setDisplayAds([]);
    fetchInventory(currentTrip.pickup.lat, currentTrip.pickup.lng);
  }, [currentTrip]);

  const getInterpolatedPosition = (progress, waypoints) => {
    if (!waypoints || waypoints.length < 2) return null;
    
    const totalSegments = waypoints.length - 1;
    const segmentProgress = progress * totalSegments;
    const currentSegment = Math.min(Math.floor(segmentProgress), totalSegments - 1);
    const segmentFraction = segmentProgress - currentSegment;
    
    const start = waypoints[currentSegment];
    const end = waypoints[currentSegment + 1];
    
    return {
      lat: start.lat + (end.lat - start.lat) * segmentFraction,
      lng: start.lng + (end.lng - start.lng) * segmentFraction
    };
  };

  // Vehicle animation
  useEffect(() => {
    if (isPlaying && vehiclePosition) {
      const animate = () => {
        progressRef.current += 0.001;
        
        if (progressRef.current >= 1) {
          recordPageImpressions();
          progressRef.current = 0;
          const currentIndex = DEMO_TRIPS.findIndex(t => t.id === currentTrip.id);
          const nextTrip = DEMO_TRIPS[(currentIndex + 1) % DEMO_TRIPS.length];
          setCurrentTrip(nextTrip);
          setAdLog([]);
          return;
        }
        
        const newPos = getInterpolatedPosition(progressRef.current, currentTrip.waypoints);
        if (newPos) {
          setVehiclePosition(newPos);
          
          if (mapRef.current) {
            mapRef.current.panTo(newPos);
          }
          
          // Fetch new ads based on proximity (goes to buffer, not display)
          if (lastFetchPosition.current) {
            const distance = Math.sqrt(
              Math.pow(newPos.lat - lastFetchPosition.current.lat, 2) + 
              Math.pow(newPos.lng - lastFetchPosition.current.lng, 2)
            ) * 111000;
            
            if (distance > 500) { // Increased threshold
              fetchInventory(newPos.lat, newPos.lng);
            }
          } else {
            fetchInventory(newPos.lat, newPos.lng);
          }
        }
        
        animationRef.current = requestAnimationFrame(animate);
      };
      animationRef.current = requestAnimationFrame(animate);
    }
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, currentTrip, fetchInventory, recordPageImpressions]);

  const handleAdClick = (businessName) => {
    setClickFeedback(businessName);
    setTimeout(() => setClickFeedback(null), 2000);
  };

  const handleSaveDeal = (dealInfo) => {
    setDealFeedback(dealInfo.businessName);
    setAdLog(prev => [...prev.slice(-4), {
      time: new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      message: `💾 Deal saved: ${dealInfo.businessName}`
    }]);
    setTimeout(() => setDealFeedback(null), 3000);
  };

  const handleDealModalOpen = () => {
    setIsPaused(true);
  };

  const handleDealModalClose = () => {
    setIsPaused(false);
  };

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  if (loadError) {
    return (
      <div className="h-screen w-screen bg-gray-950 flex items-center justify-center">
        <div className="text-red-500">Error loading Google Maps</div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="h-screen w-screen bg-gray-950 flex items-center justify-center">
        <div className="text-white animate-pulse">Loading maps...</div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-gray-950 flex items-center justify-center overflow-hidden" data-testid="drive-panel-container">
      {/* Fixed Tablet Frame */}
      <div 
        className="relative bg-black rounded-lg overflow-hidden shadow-2xl"
        style={{ 
          width: `${TABLET_WIDTH}px`, 
          height: `${TABLET_HEIGHT}px`,
          maxWidth: '100vw',
          maxHeight: '100vh'
        }}
        data-testid="drive-panel"
      >
        {/* Click Feedback Toast */}
        {clickFeedback && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[9999] bg-green-500 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg animate-pulse">
            ✓ Click recorded for {clickFeedback}
          </div>
        )}

        {/* Deal Saved Feedback Toast */}
        {dealFeedback && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[9999] bg-amber-500 text-black px-4 py-3 rounded-xl text-sm font-bold shadow-lg flex items-center gap-2">
            <span className="text-lg">🎁</span>
            <div>
              <p>Deal saved to your Booma app!</p>
              <p className="text-xs font-normal">Check your app for {dealFeedback}</p>
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <div className="h-full flex flex-col p-4">
          {/* Simulation Controls */}
          <div className="absolute top-4 right-4 z-50 flex items-center gap-3">
            <div className="relative group">
              <button 
                onClick={() => setIsPlaying(!isPlaying)}
                className={`px-5 py-2.5 rounded-full text-base font-semibold transition-all shadow-lg ${
                  isPlaying 
                    ? 'bg-red-500 hover:bg-red-600 text-white' 
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
                data-testid="simulation-toggle"
              >
                {isPlaying ? "⏹ Stop" : "▶ Start"}
              </button>
              <div className="absolute top-full right-0 mt-2 w-72 bg-gray-900 text-white text-xs p-3 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none border border-gray-700">
                <p className="font-semibold mb-1">Panel Carousel Simulation</p>
                <p className="text-gray-400">Vehicle moves along route. Ads rotate every 5 seconds. Use arrows to navigate manually.</p>
                <p className="text-blue-400 mt-2 text-[10px]">Impressions: 1.5s dwell minimum, no re-count per trip</p>
              </div>
            </div>
          </div>

          {/* Ad Update Log */}
          {adLog.length > 0 && (
            <div className="absolute top-4 left-4 z-50 bg-black/80 backdrop-blur-sm rounded-lg p-3 border border-gray-700 max-w-xs">
              <p className="text-green-400 text-xs font-semibold mb-1">📍 Activity Log</p>
              {adLog.map((log, i) => (
                <p key={i} className="text-gray-300 text-[10px] truncate">
                  <span className="text-gray-500">{log.time}</span> {log.message}
                </p>
              ))}
            </div>
          )}

          {/* Impression Stats */}
          <div className="absolute bottom-20 left-4 z-50 bg-black/80 backdrop-blur-sm rounded-lg p-3 border border-gray-700">
            <p className="text-blue-400 text-xs font-semibold mb-1">📊 Impressions This Trip</p>
            <p className="text-white text-lg font-bold">{recordedImpressionsRef.current.size} / {displayAds.length}</p>
            <p className="text-gray-500 text-xs">Page {currentPage + 1} of {totalPages}</p>
          </div>

          {/* Carousel Navigation - Left Arrow - LARGER for tablet */}
          <button
            onClick={goToPrevPage}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-50 w-16 h-16 bg-black/70 hover:bg-black/90 rounded-full flex items-center justify-center text-white transition-all shadow-xl active:scale-95"
            data-testid="prev-page-btn"
          >
            <ChevronLeft className="w-10 h-10" />
          </button>

          {/* Carousel Navigation - Right Arrow - LARGER for tablet */}
          <button
            onClick={goToNextPage}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-50 w-16 h-16 bg-black/70 hover:bg-black/90 rounded-full flex items-center justify-center text-white transition-all shadow-xl active:scale-95"
            data-testid="next-page-btn"
          >
            <ChevronRight className="w-10 h-10" />
          </button>

          {/* 2x2 Grid of Ad Tiles */}
          <div className="flex-1 grid grid-cols-2 grid-rows-2 gap-4 mx-20 my-2">
            {[0, 1, 2, 3].map(position => {
              const ad = currentPageAds[position];
              return (
                <div key={`${currentPage}-${position}`} className="min-h-0">
                  <AdTile 
                    ad={ad || { business_name: "Booma", creative: { file_url: BOOMA_LOGO_INVERTED } }}
                    position={currentPage * ADS_PER_PAGE + position}
                    boomaLogo={BOOMA_LOGO_INVERTED} 
                    onAdClick={handleAdClick}
                    onSaveDeal={handleSaveDeal}
                    onDealModalOpen={handleDealModalOpen}
                    onDealModalClose={handleDealModalClose}
                    tripId={currentTrip.id}
                  />
                </div>
              );
            })}
          </div>

          {/* Page Indicator */}
          <PageIndicator currentPage={currentPage} totalPages={totalPages} />

          {/* Centered Map Overlay - Google Maps */}
          <div 
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-40 pointer-events-none"
            style={{ 
              width: '140px', 
              height: '280px',
              filter: 'drop-shadow(0 25px 50px rgba(0,0,0,0.9))'
            }}
          >
            {/* Phone Frame - Black bezel design */}
            <div className="w-full h-full bg-black rounded-[28px] p-1.5 border-2 border-gray-800 shadow-inner relative">
              {/* Notch */}
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-1 bg-gray-900 rounded-full" style={{ zIndex: 9999 }}></div>
              
              {/* Map Container */}
              <div className="relative w-full h-full rounded-[22px] overflow-hidden bg-gray-950 pointer-events-auto">
                {vehiclePosition && (
                  <GoogleMap
                    mapContainerStyle={{ width: "100%", height: "100%" }}
                    center={vehiclePosition}
                    zoom={14}
                    onLoad={onMapLoad}
                    options={cleanMapOptions}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Spotify Player Bar with Time */}
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gray-900/95 backdrop-blur-sm flex items-center px-4 border-t border-gray-800">
          {/* Time Display */}
          <div className="flex items-center gap-2 mr-4 pr-4 border-r border-gray-700">
            <span className="text-white font-mono text-xl tabular-nums">
              {time.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>

          {/* Spotify Logo */}
          <div className="w-10 h-10 rounded-full bg-[#1DB954] flex items-center justify-center mr-3 flex-shrink-0">
            <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
            </svg>
          </div>

          {/* Track Info */}
          <div className="flex-1 min-w-0">
            <p className="text-white font-medium text-sm truncate">{spotifyTrack.title}</p>
            <p className="text-gray-400 text-xs truncate">{spotifyTrack.artist}</p>
          </div>

          {/* Progress Bar */}
          <div className="w-32 mx-4">
            <div className="h-1 bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#1DB954] rounded-full transition-all duration-1000"
                style={{ width: `${spotifyTrack.progress}%` }}
              />
            </div>
            <p className="text-gray-500 text-[10px] text-center mt-0.5">1:56</p>
          </div>

          {/* Playback Controls */}
          <div className="flex items-center gap-2">
            <button className="text-gray-400 hover:text-white transition-colors p-1">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
              </svg>
            </button>
            <button className="w-10 h-10 rounded-full bg-white flex items-center justify-center hover:scale-105 transition-transform">
              <svg className="w-4 h-4 text-black" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
              </svg>
            </button>
            <button className="text-gray-400 hover:text-white transition-colors p-1">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/>
              </svg>
            </button>
          </div>

          {/* Volume */}
          <button className="text-gray-400 hover:text-white transition-colors p-1 ml-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
